import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleStop,
  Copy,
  Database,
  FileJson,
  FileText,
  FolderOpen,
  GitBranch,
  Play,
  Layers3,
  LayoutTemplate,
  Loader2,
  Save,
  Search,
  Trash2,
  Settings2,
  Sparkles,
  Waypoints,
  Workflow,
  X,
  Zap,
  FolderKanban,
} from 'lucide-react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight, vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import { useNavigate } from 'react-router-dom';
import './Sidebar.css';
import { SAMPLE_WORKFLOW_CATALOG, type SampleWorkflowId } from '../sampleWorkflowTemplates';
import {
  SIDEBAR_CATEGORIES,
  SIDEBAR_DRAG_MIME,
  STATIC_FLOW_END_DRAG_PAYLOAD,
  type SidebarCategoryId,
  type SidebarDragPayload,
  type SidebarPaletteItem,
} from './sidebarPalette';
import { setPaletteDragCategory } from '../paletteDragContext';
import { fetchWorkflowBuilderControls, type WorkflowSidebarPaletteState } from '../workflowBuilderControlsService';
import {
  fetchWorkflowProjects,
  postWorkflow,
  putWorkflow,
  workflowApiErrorMessage,
  type PostWorkflowRequest,
  type WorkflowProjectDto,
} from '../postWorkflowService';
import WorkflowTestRunDialog, { type WorkflowTestRunPayload } from './WorkflowTestRunDialog';
SyntaxHighlighter.registerLanguage('json', json);

export {
  SIDEBAR_CATEGORIES,
  SIDEBAR_CATEGORY_LABELS,
  SIDEBAR_CATEGORY_NODE_TYPES,
  SIDEBAR_CATEGORY_ICONS,
  SIDEBAR_COLLAPSIBLE_CATEGORY_ORDER,
  SIDEBAR_DRAG_MIME,
  SIDEBAR_PALETTE_ITEMS,
  STATIC_FLOW_END_DRAG_PAYLOAD,
  buildCategoryNodeTypesFromItems,
} from './sidebarPalette';

export type {
  SidebarCategoryId,
  SidebarDragPayload,
  SidebarPaletteItem,
  WorkflowRegistryFields,
} from './sidebarPalette';

export type { WorkflowTestRunPayload } from './WorkflowTestRunDialog';

const WORKFLOW_ICON_OPTIONS: { value: string; label: string; Icon: LucideIcon }[] = [
  { value: 'Workflow', label: 'Workflow', Icon: Workflow },
  { value: 'GitBranch', label: 'Branching', Icon: GitBranch },
  { value: 'Waypoints', label: 'Waypoints', Icon: Waypoints },
  { value: 'Zap', label: 'Automation', Icon: Zap },
  { value: 'Layers3', label: 'Pipeline', Icon: Layers3 },
  { value: 'Sparkles', label: 'Intelligent', Icon: Sparkles },
  { value: 'Bot', label: 'Agentic', Icon: Bot },
  { value: 'Settings2', label: 'Agent Orchestrations', Icon: Settings2 },
  { value: 'Database', label: 'Data Flow', Icon: Database },
  { value: 'FileText', label: 'Process', Icon: FileText },
];

const PROJECT_ICON_EXTRA: Record<string, LucideIcon> = {
  FolderKanban,
};

/** Icons for project picker rows (workflow icons + extras used by project API). */
function SidebarProjectPickerIcon({ iconRef }: { iconRef?: string }) {
  const v = (iconRef ?? 'Workflow').trim();
  const opt = WORKFLOW_ICON_OPTIONS.find((x) => x.value === v);
  if (opt) {
    const Cmp = opt.Icon;
    return <Cmp size={16} strokeWidth={2} className="sidebar-icon-combo-trigger-ico" aria-hidden />;
  }
  const Extra = PROJECT_ICON_EXTRA[v];
  if (Extra) {
    return <Extra size={16} strokeWidth={2} className="sidebar-icon-combo-trigger-ico" aria-hidden />;
  }
  return <Workflow size={16} strokeWidth={2} className="sidebar-icon-combo-trigger-ico" aria-hidden />;
}

function itemMatchesSearch(
  item: SidebarPaletteItem,
  q: string,
  categoryLabels: Record<SidebarCategoryId, string>
): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  const catTitle = (categoryLabels[item.category] ?? '').toLowerCase();
  if (item.kind === 'node') {
    return (
      item.label.toLowerCase().includes(needle) ||
      item.type.toLowerCase().includes(needle) ||
      catTitle.includes(needle)
    );
  }
  return item.label.toLowerCase().includes(needle) || catTitle.includes(needle);
}

export type RemoteWorkflowPrefill = {
  name: string;
  icon: string;
  description: string;
  notes: string;
  lastComments: string;
  schemaVersion: number;
  isActive: boolean;
  /** Existing workflow project UUID when editing */
  projectUuid?: string;
};

export type SidebarProps = {
  onApplySampleWorkflow?: (id: SampleWorkflowId) => void;
  onClearCanvas?: () => void;
  /** Called after the test-run dialog collects trigger variables (manual trigger). Payload is omitted if the dialog is cancelled. */
  onTestWorkflow?: (payload?: WorkflowTestRunPayload) => void;
  /** Serializes current graph + viewport for POST/PUT `definition`; FileJson toolbar opens a preview modal. */
  buildWorkflowSaveDefinition?: () => string;
  /** Execution document v1 JSON (derived from the canvas) for the JSON modal “Execution” tab. */
  buildExecutionSchemaPreview?: () => string;
  /** When set, save uses PUT /api/post-workflow/{id} instead of POST. */
  editingWorkflowId?: string | null;
  /** Route workflow id (`/flowbuilder/:id`) — update-mode toolbar when present. */
  persistedWorkflowRouteId?: string | null;
  /** Prefills the save dialog from GET /api/post-workflow/{id}. */
  remoteWorkflowPrefill?: RemoteWorkflowPrefill | null;
  /** When opening an existing workflow from the route, block save until load succeeds. */
  remoteWorkflowLoadState?: 'none' | 'loading' | 'ready' | 'error';
};

const Sidebar: React.FC<SidebarProps> = ({
  onApplySampleWorkflow,
  onClearCanvas,
  onTestWorkflow,
  buildWorkflowSaveDefinition,
  buildExecutionSchemaPreview,
  editingWorkflowId,
  persistedWorkflowRouteId,
  remoteWorkflowPrefill,
  remoteWorkflowLoadState = 'none',
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [palette, setPalette] = useState<WorkflowSidebarPaletteState | null>(null);
  const [showSamplesModal, setShowSamplesModal] = useState(false);
  const [showSchemaModal, setShowSchemaModal] = useState(false);
  const [schemaJsonTab, setSchemaJsonTab] = useState<'execution' | 'canvas'>('execution');
  const [schemaCopied, setSchemaCopied] = useState(false);
  const [schemaModalLightSyntax, setSchemaModalLightSyntax] = useState(
    () => typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'white'
  );
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showTestRunModal, setShowTestRunModal] = useState(false);
  const [workflowDisplayName, setWorkflowDisplayName] = useState('');
  const [workflowIcon, setWorkflowIcon] = useState('Workflow');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [workflowNotes, setWorkflowNotes] = useState('');
  const [workflowLastComments, setWorkflowLastComments] = useState('');
  const [workflowIsActive, setWorkflowIsActive] = useState(true);
  const [saveSubmitting, setSaveSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [testRunPreparing, setTestRunPreparing] = useState(false);
  const [testRunPrepareError, setTestRunPrepareError] = useState<string | null>(null);
  const [iconMenuOpen, setIconMenuOpen] = useState(false);
  const iconComboRef = useRef<HTMLDivElement>(null);
  const [workflowProjectUuid, setWorkflowProjectUuid] = useState('');
  const [workflowProjects, setWorkflowProjects] = useState<WorkflowProjectDto[]>([]);
  const [loadingWorkflowProjects, setLoadingWorkflowProjects] = useState(false);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const projectComboRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetchWorkflowBuilderControls().then((next) => {
      if (!cancelled) setPalette(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setSchemaModalLightSyntax(el.getAttribute('data-theme') === 'white');
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  /** Compact definition JSON from parent (same as POST/PUT `definition`); refreshed while modal is open. */
  const workflowDefinitionRaw = useMemo(() => {
    if (!showSchemaModal) return '';
    return buildWorkflowSaveDefinition?.() ?? '';
  }, [showSchemaModal, buildWorkflowSaveDefinition]);

  const executionSchemaRaw = useMemo(() => {
    if (!showSchemaModal) return '';
    return buildExecutionSchemaPreview?.() ?? '';
  }, [showSchemaModal, buildExecutionSchemaPreview]);

  const workflowDefinitionPretty = useMemo(() => {
    if (!workflowDefinitionRaw) return '';
    try {
      return JSON.stringify(JSON.parse(workflowDefinitionRaw), null, 2);
    } catch {
      return workflowDefinitionRaw;
    }
  }, [workflowDefinitionRaw]);

  const executionSchemaPretty = useMemo(() => {
    if (!executionSchemaRaw) return '';
    try {
      return JSON.stringify(JSON.parse(executionSchemaRaw), null, 2);
    } catch {
      return executionSchemaRaw;
    }
  }, [executionSchemaRaw]);

  const activeSchemaRaw = schemaJsonTab === 'execution' ? executionSchemaRaw : workflowDefinitionRaw;
  const activeSchemaPretty = schemaJsonTab === 'execution' ? executionSchemaPretty : workflowDefinitionPretty;

  const hasExecutionTab = Boolean(buildExecutionSchemaPreview);
  const hasCanvasTab = Boolean(buildWorkflowSaveDefinition);

  const getTestRunDefinitionJson = useCallback(
    () => buildWorkflowSaveDefinition?.() ?? '',
    [buildWorkflowSaveDefinition]
  );

  useEffect(() => {
    if (!showSchemaModal) return;
    if (schemaJsonTab === 'execution' && !hasExecutionTab && hasCanvasTab) {
      setSchemaJsonTab('canvas');
    }
    if (schemaJsonTab === 'canvas' && !hasCanvasTab && hasExecutionTab) {
      setSchemaJsonTab('execution');
    }
  }, [showSchemaModal, schemaJsonTab, hasExecutionTab, hasCanvasTab]);

  const { collapsibleCategoryOrder, categoryLabels, categoryIcons, paletteItems } = palette ?? {
    collapsibleCategoryOrder: [] as SidebarCategoryId[],
    categoryLabels: {} as Record<SidebarCategoryId, string>,
    categoryIcons: {} as Record<SidebarCategoryId, LucideIcon>,
    paletteItems: [] as SidebarPaletteItem[],
  };

  const [openSections, setOpenSections] = useState<Record<SidebarCategoryId, boolean>>(
    () => ({}) as Record<SidebarCategoryId, boolean>
  );

  useEffect(() => {
    if (collapsibleCategoryOrder.length === 0) return;
    setOpenSections((prev) => {
      const next = { ...prev } as Record<SidebarCategoryId, boolean>;
      for (const c of collapsibleCategoryOrder) {
        if (next[c] === undefined) next[c] = true;
      }
      return next;
    });
  }, [collapsibleCategoryOrder]);

  useEffect(() => {
    if (!iconMenuOpen) return;
    const onDocDown = (e: MouseEvent) => {
      if (iconComboRef.current?.contains(e.target as Node)) return;
      setIconMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [iconMenuOpen]);

  useEffect(() => {
    if (!showSaveModal) setIconMenuOpen(false);
  }, [showSaveModal]);

  useEffect(() => {
    if (!showSaveModal) setProjectMenuOpen(false);
  }, [showSaveModal]);

  useEffect(() => {
    if (!projectMenuOpen) return;
    const onDocDown = (e: MouseEvent) => {
      if (projectComboRef.current?.contains(e.target as Node)) return;
      setProjectMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [projectMenuOpen]);

  useEffect(() => {
    if (!showSaveModal) return;
    let cancelled = false;
    setLoadingWorkflowProjects(true);
    fetchWorkflowProjects()
      .then((list) => {
        if (!cancelled) setWorkflowProjects(list);
      })
      .catch(() => {
        if (!cancelled) setWorkflowProjects([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingWorkflowProjects(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showSaveModal]);

  useEffect(() => {
    if (!showSchemaModal) setSchemaCopied(false);
  }, [showSchemaModal]);

  useEffect(() => {
    if (showSaveModal) setSaveError(null);
  }, [showSaveModal]);

  const resetWorkflowMetaForm = useCallback(() => {
    setWorkflowDisplayName('');
    setWorkflowIcon('Workflow');
    setWorkflowDescription('');
    setWorkflowNotes('');
    setWorkflowLastComments('');
    setWorkflowIsActive(true);
    setWorkflowProjectUuid('');
    setSaveError(null);
  }, []);

  useEffect(() => {
    if (!remoteWorkflowPrefill) return;
    setWorkflowDisplayName(remoteWorkflowPrefill.name ?? '');
    setWorkflowIcon(remoteWorkflowPrefill.icon?.trim() ? remoteWorkflowPrefill.icon : 'Workflow');
    setWorkflowDescription(remoteWorkflowPrefill.description ?? '');
    setWorkflowNotes(remoteWorkflowPrefill.notes ?? '');
    setWorkflowLastComments(remoteWorkflowPrefill.lastComments ?? '');
    setWorkflowIsActive(remoteWorkflowPrefill.isActive !== false);
    setWorkflowProjectUuid(remoteWorkflowPrefill.projectUuid?.trim() ?? '');
  }, [remoteWorkflowPrefill]);

  useEffect(() => {
    if (remoteWorkflowPrefill != null) return;
    if (remoteWorkflowLoadState !== 'none') return;
    resetWorkflowMetaForm();
  }, [remoteWorkflowPrefill, remoteWorkflowLoadState, resetWorkflowMetaForm]);

  const filteredByCategory = useMemo(() => {
    const q = searchTerm.trim();
    const map = new Map<SidebarCategoryId, SidebarPaletteItem[]>();
    for (const c of collapsibleCategoryOrder) map.set(c, []);
    for (const item of paletteItems) {
      if (item.category === SIDEBAR_CATEGORIES.TERMINAL) continue;
      if (!itemMatchesSearch(item, q, categoryLabels)) continue;
      const bucket = map.get(item.category);
      if (bucket) bucket.push(item);
    }
    return map;
  }, [searchTerm, paletteItems, collapsibleCategoryOrder, categoryLabels]);

  const toggleSection = (id: SidebarCategoryId) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const onDragStart = (event: React.DragEvent<HTMLDivElement>, payload: SidebarDragPayload) => {
    setPaletteDragCategory(payload.category);
    event.dataTransfer.setData(SIDEBAR_DRAG_MIME, JSON.stringify(payload));
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDragEndPalette = () => {
    setPaletteDragCategory(null);
  };

  const renderPaletteRow = (item: SidebarPaletteItem, variant: 'default' | 'end' = 'default') => {
    const isAgent = item.category === SIDEBAR_CATEGORIES.AGENTS;

    if (item.kind === 'action') {
      return (
        <button
          key={item.id}
          type="button"
          className={`sidebar-action${isAgent ? ' sidebar-action--agent' : ''}`}
          onClick={() => {
            /* reserved: open connect / create flows */
          }}
        >
          <span className="sidebar-bullet" aria-hidden>
            <Circle className="sidebar-lucide sidebar-lucide--bullet" strokeWidth={2} size={8} />
          </span>
          <item.Icon className="sidebar-lucide sidebar-lucide--accent" strokeWidth={2} size={16} />
          <span className={`sidebar-action-label${isAgent ? ' sidebar-action-label--agent' : ''}`}>{item.label}</span>
        </button>
      );
    }

    const payload: SidebarDragPayload = {
      type: item.type,
      label: item.label,
      icon: item.canvasIcon,
      category: item.category,
      categoryUuid: item.categoryUuid,
      resourceCategory: item.resourceCategory,
      controlUuid: item.controlUuid,
    };

    const isEnd = variant === 'end';

    return (
      <div
        key={item.id}
        className={`sidebar-node${isEnd ? ' sidebar-node--end' : ''}${isAgent ? ' sidebar-node--agent' : ''}`}
        onDragStart={(e) => onDragStart(e, payload)}
        onDragEnd={onDragEndPalette}
        draggable
        role="listitem"
      >
        <span className="sidebar-bullet" aria-hidden>
          {isEnd ? (
            <span className="sidebar-bullet-dot sidebar-bullet-dot--filled" />
          ) : (
            <Circle className="sidebar-lucide sidebar-lucide--bullet" strokeWidth={2} size={8} />
          )}
        </span>
        <item.Icon className="sidebar-lucide" strokeWidth={2} size={16} />
        <div className={`node-label${isAgent ? ' node-label--agent' : ''}`}>{item.label}</div>
      </div>
    );
  };

  const loading = palette === null;
  const saveBlockedByRemote = remoteWorkflowLoadState === 'loading' || remoteWorkflowLoadState === 'error';
  const projectPickValid = useMemo(() => {
    const id = workflowProjectUuid.trim();
    if (!id) return false;
    if (loadingWorkflowProjects) return false;
    return workflowProjects.some((p) => p.uuid === id);
  }, [workflowProjectUuid, loadingWorkflowProjects, workflowProjects]);

  const saveDisabled =
    workflowDisplayName.trim().length === 0 ||
    workflowIcon.trim().length === 0 ||
    !projectPickValid ||
    saveBlockedByRemote;
  const isUpdate = Boolean(persistedWorkflowRouteId?.trim());
  const workflowIdForPersist = editingWorkflowId?.trim() || persistedWorkflowRouteId?.trim() || null;
  const selectedWorkflowIcon = WORKFLOW_ICON_OPTIONS.find((x) => x.value === workflowIcon) ?? WORKFLOW_ICON_OPTIONS[0];
  const SelectedWorkflowIconCmp = selectedWorkflowIcon.Icon;

  const selectedWorkflowProject = useMemo(
    () => workflowProjects.find((p) => p.uuid === workflowProjectUuid),
    [workflowProjects, workflowProjectUuid]
  );

  const onDeleteWorkflowMeta = () => {
    if (
      !window.confirm(
        'Remove all entered workflow details and close this dialog? This action cannot be undone.'
      )
    ) {
      return;
    }
    resetWorkflowMetaForm();
    setShowSaveModal(false);
  };

  const buildWorkflowPersistPayload = useCallback((): PostWorkflowRequest | null => {
    if (!buildWorkflowSaveDefinition) return null;
    return {
      schemaVersion: remoteWorkflowPrefill?.schemaVersion ?? 0,
      name: workflowDisplayName.trim(),
      icon: workflowIcon.trim(),
      description: workflowDescription.trim(),
      notes: workflowNotes.trim(),
      definition: buildWorkflowSaveDefinition(),
      workFlowJson: buildExecutionSchemaPreview?.() ?? '',
      isActive: workflowIdForPersist ? workflowIsActive : true,
      lastComments: workflowLastComments.trim(),
      project_uuid: workflowProjectUuid.trim(),
    };
  }, [
    buildWorkflowSaveDefinition,
    buildExecutionSchemaPreview,
    remoteWorkflowPrefill?.schemaVersion,
    workflowDisplayName,
    workflowIcon,
    workflowDescription,
    workflowNotes,
    workflowIsActive,
    workflowLastComments,
    workflowProjectUuid,
    workflowIdForPersist,
  ]);

  const onTestWorkflowClick = async () => {
    if (!onTestWorkflow) return;
    if (!isUpdate) {
      setShowTestRunModal(true);
      return;
    }
    if (saveBlockedByRemote || testRunPreparing) return;
    const id = workflowIdForPersist;
    if (!id) return;
    const payload = buildWorkflowPersistPayload();
    if (!payload) {
      setTestRunPrepareError('Test run is not available (definition builder missing).');
      return;
    }
    setTestRunPrepareError(null);
    setTestRunPreparing(true);
    try {
      await putWorkflow(id, payload);
      setShowTestRunModal(true);
    } catch (e) {
      setTestRunPrepareError(workflowApiErrorMessage(e));
    } finally {
      setTestRunPreparing(false);
    }
  };

  const onSaveWorkflowMeta = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saveDisabled || saveSubmitting) return;
    const payload = buildWorkflowPersistPayload();
    if (!payload) {
      setSaveError('Save is not available (definition builder missing).');
      return;
    }
    setSaveError(null);
    setSaveSubmitting(true);
    try {
      if (editingWorkflowId?.trim()) {
        await putWorkflow(editingWorkflowId.trim(), payload);
        resetWorkflowMetaForm();
        setShowSaveModal(false);
      } else {
        const created = await postWorkflow(payload);
        const newId = created?.id?.trim();
        if (!newId) {
          setSaveError('Save succeeded but no workflow id was returned.');
          return;
        }
        resetWorkflowMetaForm();
        setShowSaveModal(false);
        navigate(`/portal/flowbuilder/${encodeURIComponent(newId)}`, { replace: true });
      }
    } catch (e) {
      setSaveError(workflowApiErrorMessage(e));
    } finally {
      setSaveSubmitting(false);
    }
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-topbar">
        <header className="sidebar-heading">
          <span className="sidebar-heading__icon" aria-hidden>
            <Workflow size={20} strokeWidth={1.5} />
          </span>
          <div className="sidebar-heading__text">
            <span className="sidebar-heading__eyebrow">Flow editor</span>
            <h2 className="sidebar-title">Workflow Builder</h2>
          </div>
        </header>
        <div className="sidebar-toolbar-row" role="toolbar" aria-label="Workflow actions">
          {onApplySampleWorkflow && (
            <button
              type="button"
              className="sidebar-toolbar-btn sidebar-toolbar-btn--samples"
              onClick={() => setShowSamplesModal(true)}
              aria-label="Open sample workflows"
              title="Sample workflows"
            >
              <FolderOpen size={16} strokeWidth={2} />
            </button>
          )}
          <button
            type="button"
            className="sidebar-toolbar-btn sidebar-toolbar-btn--schema"
            onClick={() => setShowSchemaModal(true)}
            aria-label="View workflow JSON (execution schema or canvas)"
            title="Execution schema / canvas JSON"
            disabled={!hasExecutionTab && !hasCanvasTab}
          >
            <FileJson size={16} strokeWidth={2} />
          </button>
          <button
            type="button"
            className="sidebar-toolbar-btn sidebar-toolbar-btn--save"
            onClick={() => setShowSaveModal(true)}
            aria-label="Open workflow save dialog"
            title={
              remoteWorkflowLoadState === 'loading'
                ? 'Loading workflow…'
                : remoteWorkflowLoadState === 'error'
                  ? 'Fix load error before saving'
                  : 'Save workflow'
            }
            disabled={saveBlockedByRemote}
          >
            <Save size={16} strokeWidth={2} />
          </button>
          {onClearCanvas && (
            <button
              type="button"
              className="sidebar-toolbar-btn sidebar-toolbar-btn--clear"
              onClick={onClearCanvas}
              aria-label="Clear workflow canvas"
              title="Clear canvas"
            >
              <Trash2 size={16} strokeWidth={2} />
            </button>
          )}
          {onTestWorkflow && (
            <button
              type="button"
              className="sidebar-toolbar-btn sidebar-toolbar-btn--test"
              onClick={() => void onTestWorkflowClick()}
              aria-label="Test workflow"
              title={
                testRunPrepareError ??
                (testRunPreparing
                  ? 'Saving workflow…'
                  : remoteWorkflowLoadState === 'loading'
                    ? 'Loading workflow…'
                    : remoteWorkflowLoadState === 'error'
                      ? 'Fix load error before testing'
                      : 'Test workflow')
              }
              disabled={saveBlockedByRemote || testRunPreparing}
            >
              {testRunPreparing ? (
                <Loader2 size={16} strokeWidth={2} className="sidebar-loading-icon" aria-hidden />
              ) : (
                <Play size={16} strokeWidth={2} />
              )}
            </button>
          )}
        </div>
      </div>

      <div className="sidebar-main">
        <div className="sidebar-search-wrap">
          <Search className="sidebar-search-icon" strokeWidth={2} size={16} aria-hidden />
          <input
            className="sidebar-search"
            type="search"
            placeholder="Search nodes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search nodes"
          />
        </div>

        <div className="sidebar-body">
          {loading && (
            <div className="sidebar-loading" role="status" aria-live="polite">
              <Loader2 className="sidebar-loading-icon" size={20} strokeWidth={2} aria-hidden />
              <span>Loading palette…</span>
            </div>
          )}

          {!loading &&
            collapsibleCategoryOrder.map((categoryId) => {
              const items = filteredByCategory.get(categoryId) ?? [];
              if (items.length === 0) return null;

              const hasQuery = searchTerm.trim().length > 0;
              const expanded = hasQuery || openSections[categoryId];
              const SectionIcon = categoryIcons[categoryId] ?? Circle;

              return (
                <section key={categoryId} className="sidebar-section">
                  <button
                    type="button"
                    className="sidebar-section-header"
                    onClick={() => toggleSection(categoryId)}
                    aria-expanded={expanded}
                  >
                    {expanded ? (
                      <ChevronDown className="sidebar-chevron" size={16} strokeWidth={2} />
                    ) : (
                      <ChevronRight className="sidebar-chevron" size={16} strokeWidth={2} />
                    )}
                    <SectionIcon className="sidebar-section-icon" strokeWidth={2} size={16} />
                    <span className="sidebar-section-title">{categoryLabels[categoryId] ?? categoryId}</span>
                  </button>
                  {expanded && <div className="sidebar-section-list">{items.map((i) => renderPaletteRow(i))}</div>}
                </section>
              );
            })}
        </div>

        {!loading && (
          <div className="sidebar-terminal" role="region" aria-label="End workflow node">
            <div
              className="sidebar-node sidebar-node--end sidebar-terminal-end"
              draggable
              onDragStart={(e) => onDragStart(e, STATIC_FLOW_END_DRAG_PAYLOAD)}
              onDragEnd={onDragEndPalette}
              role="listitem"
            >
              <span className="sidebar-bullet" aria-hidden>
                <span className="sidebar-bullet-dot sidebar-bullet-dot--filled" />
              </span>
              <CircleStop className="sidebar-lucide sidebar-terminal-end__icon" strokeWidth={2} size={20} aria-hidden />
              <div className="node-label">End</div>
            </div>
          </div>
        )}
      </div>

      {showTestRunModal && onTestWorkflow ? (
        <WorkflowTestRunDialog
          open={showTestRunModal}
          onClose={() => setShowTestRunModal(false)}
          getDefinitionJson={getTestRunDefinitionJson}
          workflowId={editingWorkflowId ?? null}
          onRunTest={(payload) => onTestWorkflow(payload)}
        />
      ) : null}

      {showSchemaModal && (
        <div
          className="sidebar-modal-backdrop"
          role="presentation"
          onClick={() => setShowSchemaModal(false)}
        >
          <div
            className="sidebar-modal sidebar-modal--schema"
            role="dialog"
            aria-modal="true"
            aria-label="Workflow JSON preview"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sidebar-schema-head">
              <div className="sidebar-schema-head-text">
                <h3 className="sidebar-schema-title">
                  {schemaJsonTab === 'execution' ? 'Execution schema (v1)' : 'Canvas definition (React Flow)'}
                </h3>
                <p className="sidebar-schema-subtitle">
                  {schemaJsonTab === 'execution' ? (
                    <>
                      Derived execution document (<code>$schema: execution/v1</code>) built from the current canvas:
                      entrypoint, <code>nodes</code>, tools, and edges. Copy uses compact JSON.
                    </>
                  ) : (
                    <>
                      Exact <code>definition</code> string sent on save: nodes, edges, and viewport (
                      <code>format: &quot;reactflow&quot;</code>). Copy uses the same compact JSON as the API.
                    </>
                  )}
                </p>
                {hasExecutionTab && hasCanvasTab ? (
                  <div className="sidebar-schema-tabs" role="tablist" aria-label="JSON view">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={schemaJsonTab === 'execution'}
                      className={`sidebar-schema-tab${schemaJsonTab === 'execution' ? ' sidebar-schema-tab--active' : ''}`}
                      onClick={() => setSchemaJsonTab('execution')}
                    >
                      Execution v1
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={schemaJsonTab === 'canvas'}
                      className={`sidebar-schema-tab${schemaJsonTab === 'canvas' ? ' sidebar-schema-tab--active' : ''}`}
                      onClick={() => setSchemaJsonTab('canvas')}
                    >
                      Canvas (save)
                    </button>
                  </div>
                ) : null}
              </div>
              <div className="sidebar-schema-actions">
                <button
                  type="button"
                  className={`sidebar-schema-copy-btn${schemaCopied ? ' sidebar-schema-copy-btn--done' : ''}`}
                  disabled={!activeSchemaRaw}
                  onClick={() => {
                    if (!activeSchemaRaw) return;
                    void navigator.clipboard.writeText(activeSchemaRaw).then(() => {
                      setSchemaCopied(true);
                      window.setTimeout(() => setSchemaCopied(false), 2000);
                    });
                  }}
                >
                  {schemaCopied ? (
                    <>
                      <Check size={14} strokeWidth={2.5} aria-hidden />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy size={14} strokeWidth={2} aria-hidden />
                      Copy JSON
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="sidebar-schema-close"
                  onClick={() => setShowSchemaModal(false)}
                  aria-label="Close schema viewer"
                >
                  <X size={18} strokeWidth={2} />
                </button>
              </div>
            </div>
            <div className="sidebar-schema-body">
              <div className="sidebar-schema-code-outer">
                <div className="sidebar-schema-code-scroll">
                  {activeSchemaPretty ? (
                    <SyntaxHighlighter
                      language="json"
                      style={schemaModalLightSyntax ? oneLight : vscDarkPlus}
                      customStyle={{
                        margin: 0,
                        padding: '14px 16px',
                        background: 'transparent',
                        fontSize: 11,
                        lineHeight: 1.5,
                        minHeight: '100%',
                      }}
                      showLineNumbers={false}
                      codeTagProps={{
                        style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
                      }}
                    >
                      {activeSchemaPretty}
                    </SyntaxHighlighter>
                  ) : (
                    <div className="sidebar-schema-empty">No JSON available for this view.</div>
                  )}
                </div>
              </div>
              <p className="sidebar-schema-footnote">
                {schemaJsonTab === 'execution'
                  ? 'Execution preview refreshes as you edit the canvas. It is not the same blob as Save.'
                  : 'Stays in sync with the canvas while this dialog is open (same payload as Save).'}
              </p>
            </div>
          </div>
        </div>
      )}

      {showSamplesModal && onApplySampleWorkflow && (
        <div className="sidebar-modal-backdrop" role="presentation" aria-hidden="true">
          <div
            className="sidebar-modal sidebar-modal--samples"
            role="dialog"
            aria-modal="true"
            aria-label="Sample workflows"
          >
            <button
              type="button"
              className="sidebar-samples-close"
              onClick={() => setShowSamplesModal(false)}
              aria-label="Close sample workflows"
            >
              <X size={18} strokeWidth={2} />
            </button>
            <div className="sidebar-modal-head">
              <h3 className="sidebar-modal-title">Sample workflows</h3>
              <p className="sidebar-modal-subtitle">
                Choose a template to load on the canvas. Your current graph will be replaced after you confirm.
              </p>
            </div>
            <div className="sidebar-sample-grid">
              {SAMPLE_WORKFLOW_CATALOG.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  className="sidebar-sample-card"
                  onClick={() => {
                    if (!window.confirm('Replace the current canvas with this sample workflow?')) return;
                    onApplySampleWorkflow(w.id);
                    setShowSamplesModal(false);
                  }}
                >
                  <span className="sidebar-sample-card-icon" aria-hidden>
                    <LayoutTemplate size={20} strokeWidth={2} />
                  </span>
                  <span className="sidebar-sample-card-title">{w.title}</span>
                  <span className="sidebar-sample-card-desc">{w.description}</span>
                  <span className="sidebar-sample-card-cta">Open template</span>
                </button>
              ))}
            </div>
            <div className="sidebar-samples-footer">
              <button type="button" className="sidebar-modal-btn" onClick={() => setShowSamplesModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaveModal && (
        <div className="sidebar-modal-backdrop" role="presentation" aria-hidden="true">
          <div
            className="sidebar-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Save workflow"
            aria-busy={saveSubmitting}
          >
            <div className="sidebar-modal-head">
              <h3 className="sidebar-modal-title">{isUpdate ? 'Update workflow' : 'Save Workflow'}</h3>
              <p className="sidebar-modal-subtitle">
                {isUpdate
                  ? 'Save changes back to the server.'
                  : 'Create a polished workflow card for listing and reuse.'}
              </p>
            </div>
            {saveError ? (
              <p className="sidebar-modal-error" role="alert">
                {saveError}
              </p>
            ) : null}
            <form className="sidebar-modal-form" onSubmit={onSaveWorkflowMeta}>
              <span className="sidebar-modal-label" id="workflow-project-label">
                Project <span className="sidebar-modal-required" aria-hidden>*</span>
              </span>
              <div className="sidebar-icon-combo" ref={projectComboRef}>
                <button
                  type="button"
                  id="workflow-project-trigger"
                  className={`sidebar-icon-combo-trigger${projectMenuOpen ? ' sidebar-icon-combo-trigger--open' : ''}${loadingWorkflowProjects ? ' sidebar-icon-combo-trigger--disabled' : ''}`}
                  aria-haspopup="listbox"
                  aria-expanded={projectMenuOpen}
                  aria-labelledby="workflow-project-label workflow-project-trigger"
                  aria-controls="workflow-project-listbox"
                  disabled={loadingWorkflowProjects}
                  onClick={() => {
                    if (loadingWorkflowProjects) return;
                    setIconMenuOpen(false);
                    setProjectMenuOpen((o) => !o);
                  }}
                >
                  <span className="sidebar-icon-combo-trigger-main">
                    {loadingWorkflowProjects ? (
                      <>
                        <Loader2 size={16} strokeWidth={2} className="sidebar-icon-combo-trigger-ico animate-spin" aria-hidden />
                        <span>Loading projects…</span>
                      </>
                    ) : selectedWorkflowProject ? (
                      <>
                        <SidebarProjectPickerIcon iconRef={selectedWorkflowProject.icon} />
                        <span>{selectedWorkflowProject.name}</span>
                      </>
                    ) : (
                      <>
                        <Workflow size={16} strokeWidth={2} className="sidebar-icon-combo-trigger-ico opacity-50" aria-hidden />
                        <span className="sidebar-modal-placeholder">Select a project</span>
                      </>
                    )}
                  </span>
                  <ChevronDown size={16} strokeWidth={2} className="sidebar-icon-combo-chevron" aria-hidden />
                </button>
                {projectMenuOpen && !loadingWorkflowProjects && (
                  <div
                    id="workflow-project-listbox"
                    className="sidebar-icon-combo-list sidebar-icon-combo-list--projects"
                    role="listbox"
                    aria-label="Workflow project"
                  >
                    {workflowProjects.length === 0 ? (
                      <div className="sidebar-project-empty">
                        No projects found. Create one from{' '}
                        <strong>Orchestrations → Manage projects</strong>.
                      </div>
                    ) : (
                      workflowProjects.map((p) => {
                        const selected = workflowProjectUuid === p.uuid;
                        return (
                          <button
                            key={p.uuid}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={`sidebar-icon-combo-option sidebar-icon-combo-option--project${selected ? ' sidebar-icon-combo-option--selected' : ''}`}
                            onClick={() => {
                              setWorkflowProjectUuid(p.uuid);
                              setProjectMenuOpen(false);
                            }}
                          >
                            <SidebarProjectPickerIcon iconRef={p.icon} />
                            <span className="sidebar-project-option-text">
                              <span className="sidebar-project-option-name">{p.name}</span>
                              {(p.description ?? '').trim() ? (
                                <span className="sidebar-project-option-desc">{p.description}</span>
                              ) : null}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
              <input type="hidden" name="project_uuid" value={workflowProjectUuid} readOnly aria-hidden />

              <label className="sidebar-modal-label" htmlFor="workflow-display-name">
                Display Name
              </label>
              <input
                id="workflow-display-name"
                className="sidebar-modal-input"
                value={workflowDisplayName}
                onChange={(e) => setWorkflowDisplayName(e.target.value)}
                placeholder="My workflow"
                required
              />

              <span className="sidebar-modal-label" id="workflow-icon-label">
                Icon
              </span>
              <div className="sidebar-icon-combo" ref={iconComboRef}>
                <button
                  type="button"
                  id="workflow-icon-trigger"
                  className={`sidebar-icon-combo-trigger${iconMenuOpen ? ' sidebar-icon-combo-trigger--open' : ''}`}
                  aria-haspopup="listbox"
                  aria-expanded={iconMenuOpen}
                  aria-labelledby="workflow-icon-label workflow-icon-trigger"
                  aria-controls="workflow-icon-listbox"
                  onClick={() => {
                    setProjectMenuOpen(false);
                    setIconMenuOpen((o) => !o);
                  }}
                >
                  <span className="sidebar-icon-combo-trigger-main">
                    <SelectedWorkflowIconCmp size={16} strokeWidth={2} className="sidebar-icon-combo-trigger-ico" />
                    <span>{selectedWorkflowIcon.label}</span>
                  </span>
                  <ChevronDown size={16} strokeWidth={2} className="sidebar-icon-combo-chevron" aria-hidden />
                </button>
                {iconMenuOpen && (
                  <div
                    id="workflow-icon-listbox"
                    className="sidebar-icon-combo-list"
                    role="listbox"
                    aria-label="Workflow icon"
                  >
                    {WORKFLOW_ICON_OPTIONS.map((opt) => {
                      const selected = workflowIcon === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          className={`sidebar-icon-combo-option${selected ? ' sidebar-icon-combo-option--selected' : ''}`}
                          onClick={() => {
                            setWorkflowIcon(opt.value);
                            setIconMenuOpen(false);
                          }}
                        >
                          <opt.Icon size={16} strokeWidth={2} className="sidebar-icon-combo-option-ico" />
                          <span>{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <input type="hidden" name="workflow_icon" value={workflowIcon} readOnly aria-hidden />

              <label className="sidebar-modal-label" htmlFor="workflow-description">
                Small Description
              </label>
              <textarea
                id="workflow-description"
                className="sidebar-modal-input sidebar-modal-textarea"
                rows={2}
                value={workflowDescription}
                onChange={(e) => setWorkflowDescription(e.target.value)}
                placeholder="Short summary"
              />

              <label className="sidebar-modal-label" htmlFor="workflow-notes">
                Notes
              </label>
              <textarea
                id="workflow-notes"
                className="sidebar-modal-input sidebar-modal-textarea"
                rows={3}
                value={workflowNotes}
                onChange={(e) => setWorkflowNotes(e.target.value)}
                placeholder="Additional notes"
              />

              <label className="sidebar-modal-label" htmlFor="workflow-last-comments">
                 comments
              </label>
              <textarea
                id="workflow-last-comments"
                className="sidebar-modal-input sidebar-modal-textarea"
                rows={2}
                value={workflowLastComments}
                onChange={(e) => setWorkflowLastComments(e.target.value)}
                placeholder="Optional comment for this save (audit / revision note)"
              />

              {isUpdate ? (
                <label className="sidebar-modal-label sidebar-modal-label--checkbox" htmlFor="workflow-is-active">
                  <input
                    id="workflow-is-active"
                    type="checkbox"
                    checked={workflowIsActive}
                    onChange={(e) => setWorkflowIsActive(e.target.checked)}
                  />
                  <span>Workflow is active</span>
                </label>
              ) : null}

              <div className="sidebar-modal-actions">
                <button
                  type="submit"
                  className="sidebar-modal-btn sidebar-modal-btn--primary"
                  disabled={saveDisabled || saveSubmitting}
                >
                  {saveSubmitting ? 'Saving…' : isUpdate ? 'Update' : 'Save'}
                </button>
                <button
                  type="button"
                  className="sidebar-modal-btn"
                  disabled={saveSubmitting}
                  onClick={() => setShowSaveModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="sidebar-modal-btn sidebar-modal-btn--danger"
                  disabled={saveSubmitting}
                  onClick={onDeleteWorkflowMeta}
                >
                  Delete
                </button>
              </div>
            </form>
            {saveSubmitting ? (
              <div className="sidebar-modal-saving-overlay" role="status" aria-live="polite">
                <Loader2 className="sidebar-modal-saving-spinner" size={28} strokeWidth={2} aria-hidden />
                <span className="sidebar-modal-saving-text">Saving workflow…</span>
                <span className="sidebar-modal-saving-hint">Please wait for the server response.</span>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
